import { signal } from "@preact/signals";
import type { Tournament } from "../../shared/types.js";
import {
  getAuth,
  getStoredMode,
  setAuth,
  setStoredMode,
  type AppMode,
  type AuthRecord,
  type Role,
} from "./storage.js";

/**
 * Who this device is in a game, and which hat it is wearing.
 *
 * Most organisers are playing in the game they're running, which the app had no way to
 * say: the creator's device was an administrator and nothing else, and the join screen
 * — the one place you tap your name — redirects anyone who already has access. So an
 * organiser could never be a player, and every row of every scorecard stayed editable
 * for them, one mis-tap away from quietly rewriting somebody else's round.
 *
 * Two separate things fix that, and keeping them separate is the whole design:
 *
 *  - the **seat** (`seatId`) — which name on the roster the organiser plays under. That
 *    belongs to the tournament, not to a browser, so it survives a new phone and shows
 *    up as claimed on the join screen. It is mirrored down here by `syncOrganizerSeat`.
 *  - the **mode** — organizing or playing, right now, on this device. Device-local,
 *    because it is a view preference; the organiser's phone and their tablet can
 *    sensibly disagree.
 *
 * **Player mode is a guardrail, not a permission boundary, and nothing here should ever
 * be described as one.** This device still holds the admin token, still sends it on
 * every op, and the server still honours it — anyone who can flip this switch can flip
 * it back. What it buys is an organiser halfway down the ninth looking at a scorecard
 * that offers steppers for their own row and nobody else's. That is what was asked for:
 * protection from a slip, not from themselves.
 */
export interface AppIdentity {
  role: Role;
  /** Holds this game's admin token. True in either mode — it is a fact about the
   * device, not about what the app is currently offering. */
  isOrganizer: boolean;
  /** Show the Setup tab and the organiser-only controls. */
  canOrganize: boolean;
  /** The roster player this device's scores belong to, or null. */
  seatId: string | null;
  mode: AppMode;
  /** Only an organiser holding a seat has two things to switch between. */
  canSwitch: boolean;
}

/**
 * The whole rule set, as a pure function — the storage reads live in `getIdentity`, so
 * this stays testable in a repo whose tests all run in the Workers pool with no DOM.
 */
export function resolveIdentity(
  auth: AuthRecord | null,
  storedMode: AppMode | null,
): AppIdentity | null {
  if (!auth) return null;

  if (auth.role !== "admin") {
    return {
      role: auth.role,
      isOrganizer: false,
      canOrganize: false,
      seatId: auth.playerId,
      mode: "organizer",
      canSwitch: false,
    };
  }

  const canSwitch = auth.playerId != null;
  // An organiser with no seat is always organizing. That isn't a default so much as a
  // rescue: a stored "player" outliving the seat it referred to — the row deleted, the
  // name claimed from another phone — would otherwise hide the Setup tab from the one
  // person who can reach it, with no seat left to switch away from.
  const mode: AppMode = canSwitch ? (storedMode ?? "organizer") : "organizer";

  return {
    role: "admin",
    isOrganizer: true,
    canOrganize: mode === "organizer",
    seatId: auth.playerId,
    mode,
    canSwitch,
  };
}

/** Bumped on every mode change so components re-render, the same way `themePref` does
 * for the theme toggle. Reading `.value` inside `getIdentity` is what subscribes them. */
const modeVersion = signal(0);

export function getIdentity(code: string): AppIdentity | null {
  modeVersion.value;
  return resolveIdentity(getAuth(code), getStoredMode(code));
}

export function setAppMode(code: string, mode: AppMode) {
  setStoredMode(code, mode);
  modeVersion.value++;
}

/**
 * Mirrors the tournament's seat into this device's auth record, called on every state
 * frame.
 *
 * One call covers three separate ways the seat and the device can drift apart: an
 * organiser recovering on a new phone (the recovery link has no way to know the seat,
 * so it starts null and this fills it in), somebody else claiming that name from their
 * own phone, and a roster edit made in another tab.
 *
 * Note what it doesn't touch: neither `token` nor `role` changes, so `getClient`'s reuse
 * check doesn't fire, the socket isn't torn down, and the offline outbox carries on
 * draining under an identity that can still execute everything in it.
 */
export function syncOrganizerSeat(code: string, tournament: Tournament) {
  // A snapshot cached before this field existed has no opinion about the seat, and a
  // screen paints from that cache before the socket's first frame lands. `undefined`
  // is "unknown", `null` is "not playing" — treating the first as the second would let
  // a stale cache quietly unseat someone.
  if (tournament.organizerPlayerId === undefined) return;

  const auth = getAuth(code);
  if (!auth || auth.role !== "admin") return;
  if (auth.playerId === tournament.organizerPlayerId) return;

  setAuth(code, {
    token: auth.token,
    role: auth.role,
    playerId: tournament.organizerPlayerId,
  });
  modeVersion.value++;
}
